import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDownIcon, ArrowUpIcon, DocumentTextIcon, TrashIcon, CalculatorIcon, HeartIcon, SparklesIcon } from '@heroicons/react/24/outline';
import PatientSearch, { ageText, patientName } from '../../components/patients/PatientSearch';
import QuoteEditor, { QUOTE_STATUS } from '../../components/quotes/QuoteEditor';
import PreopPanel, { PreopBadge } from '../../components/preop/PreopPanel';

const INVOICE_WORD = {
  preview: 'Draft invoice', awaiting_review: 'With billing', query: 'Query with doctor', discarded: 'Invoice discarded',
  submitted: 'Submitted', sent_to_patient: 'Sent to patient', accepted: 'Accepted', partially_paid: 'Part paid', rejected: 'Rejected', paid: 'Paid',
};
const fmtDay = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' });

/**
 * SessionPatients — the patients on a session, in theatre order, with what is
 * planned for each. Changes are saved with the session.
 *   Quote  the anaesthetic estimate for the patient to accept before surgery
 *   Pre-op the health questionnaire the patient answers before surgery
 *   Bill   opens the assistant with this booking and patient, to dictate the case
 *   Quote with assistant  the same, to make the quote by voice (onQuoteAssistant)
 *   saved: the patient_ids already stored on the session (Quote and Bill need a saved session)
 *   booking: the session; hospitals: to name its hospital on the quote
 *   onQuoteChanged(): a quote or pre-op questionnaire was saved or sent (refresh the week)
 */
const SessionPatients = ({ patients, onChange, editable, canBill, onBill, saved, booking, hospitals = [], onQuoteChanged, onQuoteAssistant }) => {
  const [quoting, setQuoting] = useState(null);
  const [preoping, setPreoping] = useState(null);
  const list = patients || [];
  const update = (i, patch) => onChange(list.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const remove = (i) => onChange(list.filter((_, j) => j !== i));
  const move = (i, dir) => {
    const next = [...list];
    const [x] = next.splice(i, 1);
    next.splice(i + dir, 0, x);
    onChange(next);
  };
  const add = (p) => onChange([...list, {
    patient_id: p.patient_id, title: p.title, first_names: p.first_names, surname: p.surname, sex: p.sex,
    date_of_birth: p.date_of_birth, payer: p.payer, scheme_name: p.scheme_name, planned_procedure: '', draft_id: null,
  }]);

  const hospital = hospitals.find((h) => h.hid === booking?.bhid);
  const bookingLabel = booking ? [fmtDay(booking.bdate), hospital ? hospital.hshort || hospital.hname : null, booking.btheatre, booking.bsurgeon].filter(Boolean).join(' · ') : '';
  const canQuote = !!booking?.bid && (editable || canBill);
  const iconBtn = 'rounded p-1 text-slate-300 hover:bg-slate-100 hover:text-brand-navy disabled:opacity-30';

  return (
    <div>
      {list.length > 0 && (
        <ol className="mb-2 divide-y divide-slate-100 rounded-lg border border-slate-200">
          {list.map((p, i) => {
            const status = p.claim_status || p.invoice_status;
            const isSaved = saved.has(p.patient_id);
            const q = p.quote ? QUOTE_STATUS[p.quote.status] : null;
            return (
              <li key={p.patient_id} className="px-3 py-2">
                <div className="flex items-start gap-2">
                  <span className="mt-0.5 w-4 text-xs font-semibold text-slate-400">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-slate-800">{patientName(p)}</div>
                    <div className="text-[11px] text-slate-500">
                      {[ageText(p.date_of_birth), p.sex, p.payer === 'private' ? 'Private' : p.scheme_name].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  {editable && (
                    <div className="flex shrink-0 gap-0.5">
                      <button type="button" onClick={() => move(i, -1)} disabled={i === 0} title="Earlier on the list" className={iconBtn}><ArrowUpIcon className="h-3.5 w-3.5" /></button>
                      <button type="button" onClick={() => move(i, 1)} disabled={i === list.length - 1} title="Later on the list" className={iconBtn}><ArrowDownIcon className="h-3.5 w-3.5" /></button>
                      <button type="button" onClick={() => remove(i)} disabled={!!p.draft_id} title={p.draft_id ? 'Already billed — can’t be taken off' : 'Take off this session'} className={`${iconBtn} hover:text-red-600`}>
                        <TrashIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>
                <div className="mt-1.5 flex items-center gap-2 pl-6">
                  {editable ? (
                    <input
                      value={p.planned_procedure || ''}
                      onChange={(e) => update(i, { planned_procedure: e.target.value })}
                      placeholder="Planned procedure"
                      className="min-w-0 flex-1 rounded-md border border-slate-200 px-2 py-1 text-xs outline-none focus:border-brand-navy"
                    />
                  ) : (
                    <span className="min-w-0 flex-1 truncate text-xs text-slate-600">{p.planned_procedure || 'No procedure noted'}</span>
                  )}
                  {p.payer !== 'private' && (editable ? (
                    <input
                      value={p.auth_number || ''}
                      onChange={(e) => update(i, { auth_number: e.target.value.toUpperCase() })}
                      placeholder="Auth no."
                      title="The medical aid's authorisation number for this admission — it goes on the claim"
                      maxLength={30}
                      className="w-28 shrink-0 rounded-md border border-slate-200 px-2 py-1 font-mono text-xs outline-none focus:border-brand-navy"
                    />
                  ) : p.auth_number ? (
                    <span className="shrink-0 font-mono text-[11px] text-slate-500" title="Authorisation number">Auth {p.auth_number}</span>
                  ) : null)}
                  {p.draft_id ? (
                    <Link to="/billing" className="shrink-0 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700 hover:underline">
                      {p.invoice_no || INVOICE_WORD[status] || 'Billed'}{p.invoice_no && status ? ` · ${INVOICE_WORD[status]}` : ''}
                    </Link>
                  ) : canBill ? (
                    <button
                      type="button"
                      onClick={() => onBill(p)}
                      disabled={!isSaved}
                      title={isSaved ? 'Bill this case with the assistant' : 'Save the session first'}
                      className="inline-flex shrink-0 items-center gap-1 rounded-md bg-brand-navy px-2.5 py-1 text-xs font-semibold text-white hover:opacity-95 disabled:opacity-40"
                    >
                      <DocumentTextIcon className="h-3.5 w-3.5" /> Bill
                    </button>
                  ) : null}
                </div>
                {canQuote && (
                  <div className="mt-1.5 flex flex-wrap items-center gap-2 pl-6">
                    {q && <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${q.tone}`}>{q.label}{p.quote.shortfall != null && ['sent', 'accepted', 'draft'].includes(p.quote.status) ? ` · patient pays ${`R${Math.round(p.quote.shortfall)}`}` : ''}</span>}
                    <button
                      type="button"
                      onClick={() => setQuoting(p)}
                      disabled={!isSaved}
                      title={isSaved ? 'Estimate the fees for the patient to accept before surgery' : 'Save the session first'}
                      className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-700 hover:border-brand-teal hover:text-brand-teal disabled:opacity-40"
                    >
                      <CalculatorIcon className="h-3.5 w-3.5" /> {p.quote ? 'Quote' : 'Make a quote'}
                    </button>
                    {canBill && onQuoteAssistant && (
                      <button
                        type="button"
                        onClick={() => onQuoteAssistant(p)}
                        disabled={!isSaved}
                        title={isSaved ? 'Make the quote with the assistant — say the procedure and time' : 'Save the session first'}
                        className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-700 hover:border-brand-teal hover:text-brand-teal disabled:opacity-40"
                      >
                        <SparklesIcon className="h-3.5 w-3.5" /> Quote with assistant
                      </button>
                    )}
                    {p.preop && <PreopBadge preop={p.preop} />}
                    <button
                      type="button"
                      onClick={() => setPreoping(p)}
                      disabled={!isSaved}
                      title={isSaved ? 'Health questions for the patient to answer before surgery' : 'Save the session first'}
                      className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-700 hover:border-brand-teal hover:text-brand-teal disabled:opacity-40"
                    >
                      <HeartIcon className="h-3.5 w-3.5" /> {p.preop ? 'Pre-op' : 'Send pre-op questions'}
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}
      {editable ? (
        <PatientSearch onPick={add} exclude={list.map((p) => p.patient_id)} />
      ) : (
        list.length === 0 && <p className="text-xs text-slate-400">No patients on this session.</p>
      )}
      {canBill && list.some((p) => !saved.has(p.patient_id)) && (
        <p className="mt-1.5 text-[11px] text-slate-500">Save the session to quote or bill the patients you’ve just added.</p>
      )}

      {quoting && (
        <QuoteEditor
          bid={booking.bid}
          patient={{ patient_id: quoting.patient_id, name: patientName(quoting), email: quoting.email, payer: quoting.payer, scheme_name: quoting.scheme_name, bmi: quoting.preop?.bmi }}
          bookingLabel={bookingLabel}
          plannedProcedure={quoting.planned_procedure}
          onClose={() => setQuoting(null)}
          onChanged={() => onQuoteChanged?.()}
        />
      )}
      {preoping && (
        <PreopPanel
          bid={booking.bid}
          patient={{ patient_id: preoping.patient_id, name: patientName(preoping), email: preoping.email }}
          bookingLabel={[bookingLabel, preoping.planned_procedure].filter(Boolean).join(' · ')}
          onClose={() => setPreoping(null)}
          onChanged={() => onQuoteChanged?.()}
        />
      )}
    </div>
  );
};

export default SessionPatients;
